import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TestModeBanner } from "@/components/TestModeBanner";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  useRouterState,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { Suspense, lazy, useEffect, type ReactNode } from "react";
import * as Sentry from "@sentry/react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { MiniCart } from "@/components/cart/MiniCart";
import { Toaster } from "sonner";
import { useStoreSettings } from "@/lib/admin/settings";
import { initMarketingCapture } from "@/lib/checkout/marketing";
import { initPostHog } from "@/lib/analytics/posthog";
import { initSentry } from "@/lib/sentry/client";
import { THEME_INIT_SCRIPT, applyTheme, readStoredTheme, useTheme } from "@/lib/theme";
// Registers the database-backed role repository for every admin surface.
// Must be an explicit call: `sideEffects: false` strips side-effect-only
// imports from the production bundle.
import { installCloudRoleRepository } from "@/lib/admin/role-repository.cloud";

installCloudRoleRepository();

// Lazy-loaded so the click-to-chat widget never blocks first paint or SEO.
const WhatsAppDirectButton = lazy(() =>
  import("@/components/support/WhatsAppDirectButton").then((m) => ({
    default: m.WhatsAppDirectButton,
  })),
);
const WhatsAppChatButton = lazy(() =>
  import("@/components/support/WhatsAppChatButton").then((m) => ({
    default: m.WhatsAppChatButton,
  })),
);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    Sentry.captureException(error, { tags: { boundary: "tanstack_root_error_component" } });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "CB16 Atelier — Luxury Perfumery" },
      {
        name: "description",
        content:
          "CB16 Atelier — hand-composed luxury fragrances, discovery packs and corporate gifting, shipped across India.",
      },
      { name: "author", content: "CB16 Atelier" },
      { property: "og:site_name", content: "CB16 Atelier" },
      { property: "og:title", content: "CB16 Atelier — Luxury Perfumery" },
      {
        property: "og:description",
        content: "Hand-composed luxury fragrances from the CB16 atelier.",
      },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_IN" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@400;500;600&display=swap",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <script dangerouslySetInnerHTML={{ __html: THEME_INIT_SCRIPT }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const settings = useStoreSettings();
  const { resolvedTheme } = useTheme();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const router = useRouter();

  // Re-evaluates on navigation so the frozen Admin console stays light.
  useEffect(() => {
    applyTheme(readStoredTheme(), pathname);
  }, [pathname]);

  useEffect(() => {
    initSentry(router);
    initMarketingCapture();
    // Single app-wide PostHog bootstrap (idempotent).
    void initPostHog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorComponent error={error as Error} reset={resetError} />
      )}
    >
    <QueryClientProvider client={queryClient}>
      <BrandingHead />
      <div className="flex min-h-dvh flex-col bg-white text-black">
        <TestModeBanner />
        {settings.storeStatus.mode !== "open" && settings.storeStatus.message && (
          <div className="bg-black text-white">
            <p className="mx-auto max-w-7xl px-6 py-2 text-center text-[11px] font-medium uppercase tracking-[0.24em]">
              {settings.storeStatus.message}
            </p>
          </div>
        )}
        <Header />
        <main className="flex-1">
          {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
          <Outlet />
        </main>
        <Footer />
        <MiniCart />
        <Toaster richColors position="top-right" closeButton theme={resolvedTheme} />
        <Suspense fallback={null}>
          <WhatsAppChatButton />
          <WhatsAppDirectButton />
        </Suspense>
      </div>
    </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

/**
 * Applies settings-driven favicon and theme-color at runtime. This keeps
 * the Media Library / Settings surfaces as the single source of truth for
 * brand assets without requiring a build to change them.
 */
function BrandingHead() {
  const settings = useStoreSettings();
  useEffect(() => {
    if (typeof document === "undefined") return;
    const setLink = (rel: string, href: string, sizes?: string, type?: string) => {
      if (!href) return;
      const selector = sizes ? `link[rel="${rel}"][sizes="${sizes}"]` : `link[rel="${rel}"]:not([sizes])`;
      let el = document.head.querySelector<HTMLLinkElement>(selector);
      if (!el) {
        el = document.createElement("link");
        el.rel = rel;
        if (sizes) el.setAttribute("sizes", sizes);
        document.head.appendChild(el);
      }
      if (type) el.type = type;
      el.href = href;
    };
    const b = settings.branding;
    if (b.favicon32) setLink("icon", b.favicon32, "32x32", "image/png");
    if (b.favicon16) setLink("icon", b.favicon16, "16x16", "image/png");
    if (b.favicon180) setLink("apple-touch-icon", b.favicon180, "180x180");
    let meta = document.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
    }
    meta.content = b.themeColor || "#0F0F10";
  }, [settings.branding]);
  return null;
}
